import { ref, reactive } from "vue";
import type { SummaryTypeRadioItem, ShopSelectValue, AreaSelectValue } from "../types";
import { DeliveryArea } from "@/generated/graphql";
import {
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { isOperatorGroupShop as checkIsOperatorGroupShop } from "@/common/business";
import { localStorageGetGroup } from "@/common/localStorageUtil";

/**
 * 検索・絞り込みで使用する選択肢データを提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useSalesSummaryFilterOptions = () => {
  //所属グループの状態
  const isOperatorGroupShop = checkIsOperatorGroupShop(localStorageGetGroup());

  const tab = ref<string>("eachDay");
  const mode = ref<"each_day" | "each_shop">("each_day");

  // v-date-picker 表示
  const selectAllShopByDayFlg = ref(true);
  const selectAllShopByShopFlg = ref(false);
  const selectEachShopByDayFlg = ref(false);

  const disabledEachShopTab = ref(false);
  const disabledShopSelect = ref(false);

  /**
   * 集計タイプ名 定義
   */
  const summaryTypeRadioItems = reactive<SummaryTypeRadioItem[]>([
    {
      index: "1",
      item: "全店舗",
      disabled: false,
    },
    {
      index: "2",
      item: "店舗別",
      disabled: false,
    },
  ]);

  const summeryTypeValues = ref<SummaryTypeRadioItem>({
    index: "0",
    item: "",
    disabled: false,
  });

  const shopSelectValues = ref<ShopSelectValue>({
    shop_code: "",
    name: "",
  });

  const areaSelectValues = ref<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });
  const areaEditSelectValues = reactive<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });

  // エリア一覧
  const areaListItems = ref<(DeliveryArea | null | undefined)[]>([]);

  /**
   * 所属グループクリック @click
   */
  const onClickSummaryType = async (index: string): Promise<void> => {
    if (!index) {
      return;
    }
    if (index === "1") {
      selectAllShopByDayFlg.value = true;
      selectAllShopByShopFlg.value = false;
      selectEachShopByDayFlg.value = false;
    } else if (index === "2") {
      selectAllShopByDayFlg.value = false;
      selectAllShopByShopFlg.value = false;
      selectEachShopByDayFlg.value = true;
    }
  };

  /**
   * 全店舗タブ @Click（リスト配列のクリアは呼び出し元で行う）
   */
  const switchToEachDayMode = (): void => {
    mode.value = "each_day";
    selectAllShopByShopFlg.value = false;
    // 現在の集計タイプに基づいて表示フラグを復元する
    // 初期化前(index === "0")の場合は全店舗(index === "1")として扱う
    const currentIndex = summeryTypeValues.value.index === "0" ? "1" : summeryTypeValues.value.index;
    onClickSummaryType(currentIndex);
  };

  /**
   * 店舗毎タブ @Click（リスト配列のクリアは呼び出し元で行う）
   */
  const switchToEachShopMode = (): void => {
    mode.value = "each_shop";
    selectAllShopByDayFlg.value = false;
    selectAllShopByShopFlg.value = true;
    selectEachShopByDayFlg.value = false;
  };

  /**
   * 「店舗」選択ボックス @change
   */
  const onChangeShop = async (): Promise<void> => {
    //店舗名が空の場合、エリア名も空にする
    if (!shopSelectValues.value || !shopSelectValues.value.shop_code) {
      areaSelectValues.value.area_code = "";
      areaSelectValues.value.area_name = "";
      areaEditSelectValues.area_code = "";
      areaEditSelectValues.area_name = "";
      areaListItems.value = [];
      return;
    }

    //graphql接続
    const apolloClient = await generateApolloClient();

    areaSelectValues.value.area_code = "";
    areaSelectValues.value.area_name = "";
    areaEditSelectValues.area_code = "";
    areaEditSelectValues.area_name = "";
    areaListItems.value = [];

    /**
     * エリアデータ取得処理
     */
    const listArea: ApolloQueryResult<ListDeliveryAreaQuery> = await apolloClient.query<
      ListDeliveryAreaQuery,
      ListDeliveryAreaQueryVariables
    >({
      query: ListDeliveryAreaDocument,
      variables: {
        chain_code: localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
        shop_code: shopSelectValues.value.shop_code,
      },
      fetchPolicy: "no-cache",
    });
    areaListItems.value = (listArea.data.listDeliveryArea ?? []) as (DeliveryArea | null | undefined)[];
  };

  return {
    isOperatorGroupShop,
    tab,
    mode,
    selectAllShopByDayFlg,
    selectAllShopByShopFlg,
    selectEachShopByDayFlg,
    disabledEachShopTab,
    disabledShopSelect,
    summaryTypeRadioItems,
    summeryTypeValues,
    shopSelectValues,
    areaSelectValues,
    areaEditSelectValues,
    areaListItems,
    onClickSummaryType,
    switchToEachDayMode,
    switchToEachShopMode,
    onChangeShop,
  };
};
